import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStudents } from "@/hooks/useStudents";
import { useDongClaims } from "@/hooks/useDongClaims";
import { useAuth } from "@/store/auth";
import { fmtYearMonth, fmtHo } from "@/lib/format";
import { optimizeRoute, packApartmentOrder, reorderAiByGeography, clusterStops, optimizeByRoadMatrix, tourLengthKm, Stop } from "@/lib/routeOptimizer";
import { fetchRoadMatrix } from "@/lib/routeMatrix";
import { ORIGIN_DEFAULT, openNavigation } from "@/lib/navigation";
import { useOrgStore } from "@/store/org";
import { geocodeAddress } from "@/lib/vworld";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { BRAND_COLORS } from "@/lib/brands";
import { Student } from "@/types";
import MapView, { Marker, Popup, Polyline, FitBoundsOnData } from "@/components/MapView";
import { numberIcon, dotIcon, truckIcon, STATUS_COLORS } from "@/lib/map";
import { useApartments } from "@/hooks/useApartments";
import { classifyLine } from "@/lib/apartmentLines";
import { encodeApartmentId, PATTERN_LABEL } from "@/lib/apartmentInfer";
import { type AiRouteGroup } from "@/lib/aiRoute";
import { householdKey } from "@/lib/household";
import { claimMatchesStudent } from "@/lib/claims";
import clsx from "clsx";

import { plainError } from "@/lib/plainError";
export default function RouteDay() {
  const { user } = useAuth();
  const ym = fmtYearMonth();
  const { students } = useStudents({ status: "active" });
  const { claims } = useDongClaims(ym);
  const apartmentPatterns = useApartments();
  const [nav, setNav] = useState<"tmap" | "kakao">("tmap");
  const [geocoding, setGeocoding] = useState(false);
  const [log, setLog] = useState("");
  const [view, setView] = useState<"list" | "map">("list");

  const myClaims = claims.filter((c) => c.driverUid === user?.uid);

  const myStudents = useMemo(
    () =>
      students.filter(
        (s) => !s.courierFlag && myClaims.some((c) => claimMatchesStudent(c, s)),
      ),
    [students, myClaims],
  );

  const withCoords = myStudents.filter((s) => s.lat && s.lng);
  const missingCoords = myStudents.filter((s) => !s.lat || !s.lng);

  // 경로 출발점 = org 창고 지도 위치(orgs 문서). 부산 창고 기본값은 1호 업체(freemilk)만 쓴다.
  // 다른 업체가 창고를 아직 안 넣었으면 내 담당 학생들의 가운데 지점에서 출발하게 해 부산에서 출발하는 일을 막는다.
  const warehouse = useOrgStore((s) => s.org?.warehouse);
  const orgId = useAuth((s) => s.orgId);
  const role = useAuth((s) => s.role);
  const noWarehouse = !warehouse && orgId !== "freemilk";
  // 가운데 지도 위치는 숫자로 먼저 구해 두고 그 값이 바뀔 때만 출발점을 새로 만든다(매 렌더마다 경로를 다시 계산하지 않게)
  const cLat = noWarehouse && withCoords.length ? +(withCoords.reduce((a, s) => a + (s.lat as number), 0) / withCoords.length).toFixed(5) : 0;
  const cLng = noWarehouse && withCoords.length ? +(withCoords.reduce((a, s) => a + (s.lng as number), 0) / withCoords.length).toFixed(5) : 0;
  const origin = useMemo(() => {
    if (warehouse) return { ...ORIGIN_DEFAULT, lat: warehouse.lat, lng: warehouse.lng };
    if (!noWarehouse || !cLat) return ORIGIN_DEFAULT;
    return { lat: cLat, lng: cLng, name: "담당 구역 가운데" };
  }, [warehouse, noWarehouse, cLat, cLng]);

  // 배송 코스 최적화는 이미 이번 달 완료된 배송을 제외하고 "미배송"만 대상으로 한다.
  const isDelivered = (s: Student) => !!s.deliveredMonths?.includes(ym);
  const pendingWithCoords = withCoords.filter((s) => !isDelivered(s));

  const tspRoute = useMemo<Stop[]>(() => {
    if (!pendingWithCoords.length) return [];
    const stops = pendingWithCoords.map((s) => ({ student: s, lat: s.lat!, lng: s.lng! }));
    const opt = optimizeRoute(origin, stops);
    return packApartmentOrder(opt, apartmentPatterns);
  }, [pendingWithCoords, apartmentPatterns, origin]);

  // [진단] 단지 묶기(packApartmentOrder)가 동선을 줄이는지/늘리는지 실데이터로 계측.
  // ?debug=route 일 때만 화면에 노출. 같은 origin·같은 직선 메트릭으로 raw(2-opt만) vs packed 비교.
  const debugRoute = new URLSearchParams(window.location.search).get("debug") === "route";
  const routeDiag = useMemo(() => {
    if (!debugRoute || !pendingWithCoords.length) return null;
    const stops = pendingWithCoords.map((s) => ({ student: s, lat: s.lat!, lng: s.lng! }));
    const raw = optimizeRoute(origin, stops);          // 2-opt 최적해(단지 묶기 전)
    const packed = packApartmentOrder(raw, apartmentPatterns); // 단지 묶기 후
    const dRaw = tourLengthKm(origin, raw);
    const dPacked = tourLengthKm(origin, packed);
    return { n: stops.length, dRaw, dPacked, delta: dPacked - dRaw };
  }, [debugRoute, pendingWithCoords, apartmentPatterns, origin]);

  // 정렬 결과를 당일·기사별로 보존 — 배송 완료 후 상세→목록 복귀(리마운트) 시 순서 유지.
  const orderKey = `route-order-${ym}-${user?.uid ?? "anon"}`;
  const restored = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(orderKey);
      if (raw) return JSON.parse(raw) as { ids?: string[]; origin?: { lat: number; lng: number } | null; reasoning?: string };
    } catch { /* 무시 */ }
    return null;
  }, [orderKey]);

  const [aiOrderIds, setAiOrderIds] = useState<string[] | null>(restored?.ids ?? null);
  const [aiReasoning, setAiReasoning] = useState<string>(restored?.reasoning ?? "");
  const [aiGroups, setAiGroups] = useState<AiRouteGroup[]>([]);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string>("");
  // 정렬 시 출발점(기사 현재 위치 GPS). 없으면 창고(ORIGIN_DEFAULT).
  const [originOverride, setOriginOverride] = useState<{ lat: number; lng: number } | null>(restored?.origin ?? null);

  // 한 번 정한 순서(aiOrderIds)는 고정. 배송 완료분만 빼고, 새 가구는 끝에 붙인다.
  // (배송할 때마다 재정렬되면 남은 집 순서가 튀므로 — 재정렬은 "최단 경로 정렬" 버튼으로만)
  useEffect(() => {
    if (aiOrderIds || !tspRoute.length) return;
    setAiOrderIds(tspRoute.map((st) => st.student.id)); // 초기 1회: 창고 기준 순서
  }, [aiOrderIds, tspRoute]);

  // 순서/출발점이 바뀌면 보존 — 리마운트되어도 기존 정렬을 그대로 복원.
  useEffect(() => {
    if (!aiOrderIds) return;
    try {
      sessionStorage.setItem(orderKey, JSON.stringify({ ids: aiOrderIds, origin: originOverride, reasoning: aiReasoning }));
    } catch { /* 무시 */ }
  }, [orderKey, aiOrderIds, originOverride, aiReasoning]);

  const route = useMemo<Stop[]>(() => {
    const byId = new Map(pendingWithCoords.map((s) => [s.id, s]));
    const out: Stop[] = [];
    const seen = new Set<string>();
    if (aiOrderIds) {
      for (const id of aiOrderIds) {
        const s = byId.get(id);
        if (s && s.lat && s.lng) { out.push({ student: s, lat: s.lat, lng: s.lng }); seen.add(id); }
      }
    }
    // 저장된 순서에 없는 미배송(새로 추가/누락)은 끝에 붙임
    for (const s of pendingWithCoords) {
      if (!seen.has(s.id) && s.lat && s.lng) out.push({ student: s, lat: s.lat, lng: s.lng });
    }
    // 같은 아파트 단지는 지도 위치가 흩어져도 항상 한 묶음으로 — AI 최단경로 정렬 후에도 유지.
    const packed = packApartmentOrder(out, apartmentPatterns);
    // 같은 가구(형제)는 정렬 순서와 무관하게 항상 인접시킴 — 빨간 테두리 묶음/한번 배송이 깨지지 않도록.
    const clustered: Stop[] = [];
    const used = new Set<number>();
    for (let i = 0; i < packed.length; i++) {
      if (used.has(i)) continue;
      clustered.push(packed[i]); used.add(i);
      const key = householdKey(packed[i].student);
      if (!key) continue;
      for (let j = i + 1; j < packed.length; j++) {
        if (!used.has(j) && householdKey(packed[j].student) === key) { clustered.push(packed[j]); used.add(j); }
      }
    }
    return clustered;
  }, [aiOrderIds, pendingWithCoords, apartmentPatterns]);

  // 지도/동선 출발점 지도 위치 — 현재 위치가 있으면 그걸, 없으면 창고.
  const effOriginCoords: [number, number] = originOverride ? [originOverride.lat, originOverride.lng] : [origin.lat, origin.lng];

  const runAiOptimize = async () => {
    if (!pendingWithCoords.length) return;
    setAiBusy(true);
    setAiError("");
    try {
      // 기사 현재 위치(GPS)를 출발점으로, 완료 제외한 남은 배송만 최단 동선으로 재정렬해 그 순서를 고정.
      let start: { lat: number; lng: number } = origin;
      let started = "창고(위치 권한 허용 시 현재 위치)";
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("위치 기능 미지원"));
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 8000 });
        });
        start = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setOriginOverride(start);
        started = "현재 위치";
      } catch {
        setOriginOverride(null); // 위치 실패 시 창고 기준
      }
      const stops = pendingWithCoords.map((s) => ({ student: s, lat: s.lat!, lng: s.lng! }));

      // 단지=가상노드로 묶어 도로거리 행렬을 작게 받고, 그 행렬로 단지 순서를 최적화.
      // 도로거리를 못 받으면(키 미인증/호출 실패) 직선거리로 폴백.
      const clusters = clusterStops(stops);
      const points = [start, ...clusters.map((c) => ({ lat: c.lat, lng: c.lng }))];
      let ordered: Stop[];
      let how: string;
      try {
        const matrix = await fetchRoadMatrix(points);
        ordered = optimizeByRoadMatrix(stops, clusters, matrix.distance, apartmentPatterns);
        if (matrix.provider === "kakao") {
          how = "도로거리(카카오) 기준";
          setAiWarnings([]);
        } else {
          how = "직선거리 기준";
          setAiWarnings([matrix.note ?? "도로거리를 못 받아 직선거리로 정렬했습니다."]);
        }
      } catch (e) {
        console.error("도로거리 행렬 호출 실패 — 직선거리 폴백", e);
        ordered = reorderAiByGeography(start, stops);
        how = "직선거리 기준";
        setAiWarnings(["도로거리 서버 호출 실패 — 직선거리로 정렬했습니다."]);
      }

      setAiOrderIds(ordered.map((o) => o.student.id));
      setAiReasoning(`${started} · ${how} · 남은 ${pendingWithCoords.length}가구를 최단 동선으로 정렬했습니다.`);
      setAiGroups([]);
    } catch (e: any) {
      console.error("경로 정렬 실패", e);
      setAiError(plainError(e, "경로를 정렬하지 못했습니다."));
    } finally {
      setAiBusy(false);
    }
  };


  const geocodeAll = async () => {
    setGeocoding(true);
    try {
      let i = 0;
      let ok = 0, held = 0, failed = 0; // 전부 실패해도 "✓ 처리"라고 뜨던 것을 고침(예행연습 02-20)
      for (const s of missingCoords) {
        i++;
        setLog(`${i}/${missingCoords.length} — ${s.school} ${s.name}`);
        try {
          const p = await geocodeAddress(s.refinedAddress || s.rawAddress);
          // roadMismatch = 입력과 다른 번길로 스냅 의심(함정 #8) — 엉뚱한 지도 위치를 쓰지 않고 관리자 확인에 맡긴다
          if (p && !p.roadMismatch) {
            await updateDoc(doc(db, "students", s.id), { lat: p.y, lng: p.x, geocodedAt: new Date() });
            ok++;
          } else if (p) held++;
          else failed++;
        } catch (e) { failed++; }
      }
      const parts = [`위치 찾음 ${ok}건`, held ? `확인 필요 ${held}건(다른 번길로 잡혀 사장님 확인)` : "", failed ? `못 찾음 ${failed}건` : ""].filter(Boolean).join(" · ");
      setLog(ok === 0 ? `⚠️ 위치를 하나도 못 찾았습니다 — ${parts}. 주소가 이상하면 사장님께 알려 주세요.` : `${ok === missingCoords.length ? "✓" : "△"} ${parts}`);
    } finally {
      setGeocoding(false);
    }
  };

  const navigate = useNavigate();

  // route 는 이미 미배송만 담고 있다. 배송완료분은 별도 카드용으로 withCoords 에서 추출.
  const pendingRoute = route;
  const doneStops = useMemo<Stop[]>(
    () => withCoords.filter(isDelivered).map((s) => ({ student: s, lat: s.lat!, lng: s.lng! })),
    [withCoords, ym],
  );
  const doneCount = useMemo(() => myStudents.filter(isDelivered).length, [myStudents, ym]);

  // 같은 가구(형제) 묶음 — 같은 집(정확한 주소 키)은 빨간 테두리로 묶어 한 번에 배송 가능함을 표시.
  const routeBlocks = useMemo(() => {
    const out: Array<
      { type: "single"; stop: Stop; index: number } | { type: "household"; stops: Stop[]; index: number }
    > = [];
    let i = 0;
    while (i < pendingRoute.length) {
      const key = householdKey(pendingRoute[i].student);
      const group = [pendingRoute[i]];
      let j = i + 1;
      while (j < pendingRoute.length && key && householdKey(pendingRoute[j].student) === key) {
        group.push(pendingRoute[j]);
        j++;
      }
      if (key && group.length >= 2) out.push({ type: "household", stops: group, index: i });
      else group.forEach((st, g) => out.push({ type: "single", stop: st, index: i + g }));
      i = j;
    }
    return out;
  }, [pendingRoute]);

  // 한 정류장의 라인 헤더 + 카드 JSX 생성 (단일/가구 공통)
  const renderStop = (stop: Stop, i: number) => {
    const prev = i > 0 ? pendingRoute[i - 1].student : null;
    const cur = stop.student;
    const aptId = cur.buildingName ? encodeApartmentId(cur.gu, cur.dong, cur.buildingName) : "";
    const pat = aptId ? apartmentPatterns.get(aptId) : undefined;
    const line = cur.buildingName ? classifyLine(cur, pat?.pattern || "1-2|3-4") : null;
    const prevLine = prev?.buildingName ? classifyLine(prev, pat?.pattern || "1-2|3-4") : null;
    const newApt = !prev || prev.buildingName !== cur.buildingName || prev.buildingDong !== cur.buildingDong;
    const newLine = newApt || (prevLine && line && prevLine.groupIndex !== line.groupIndex);
    // 이 라인(같은 동·같은 엘리베이터 코어)에 연속된 가구들의 총 박스 수
    let lineBoxes = 0;
    if (newLine && cur.buildingName && line) {
      for (let j = i; j < pendingRoute.length; j++) {
        const sj = pendingRoute[j].student;
        if (sj.buildingName !== cur.buildingName || sj.buildingDong !== cur.buildingDong) break;
        if (classifyLine(sj, pat?.pattern || "1-2|3-4").groupIndex !== line.groupIndex) break;
        lineBoxes += Number(sj.boxesPerMonth) || 1;
      }
    }
    const header =
      newLine && cur.buildingName && line ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-3 mb-1 px-1">
          <div className={clsx("h-px flex-1", line.groupIndex === 0 ? "bg-brand-200" : line.groupIndex === 1 ? "bg-amber-200" : "bg-emerald-200")} />
          <span className="font-semibold flex items-center gap-1">
            🛗 {cur.buildingName} {cur.buildingDong}동 · {line.groupLabel}
            <span className="chip ml-1 text-2xs bg-slate-700 text-white">📦 {lineBoxes}박스</span>
            {pat && (
              <span className={clsx("chip ml-1 text-2xs", pat.source === "verified" ? "chip-success" : "chip-brand")}>
                {pat.source === "verified" ? "확정" : "추론"} {PATTERN_LABEL[pat.pattern].emoji}
              </span>
            )}
          </span>
          <div className={clsx("h-px flex-1", line.groupIndex === 0 ? "bg-brand-200" : line.groupIndex === 1 ? "bg-amber-200" : "bg-emerald-200")} />
        </div>
      ) : null;
    const card = (
      <StopCard
        index={i + 1}
        stop={stop}
        lineColor={line?.groupIndex}
        onGo={() => {
          // 길찾기 → 티맵 띄우고, 사이트는 곧바로 배송 상세로 전환(복귀 시 바로 완료 처리).
          openNavigation({ lat: stop.lat, lng: stop.lng, name: stop.student.refinedAddress }, nav);
          navigate(`/driver/route/${stop.student.id}`);
        }}
        onDetail={() => navigate(`/driver/route/${stop.student.id}`)}
      />
    );
    return { header, card };
  };

  return (
    <div className="space-y-4">
      {noWarehouse && (
        <div className="card p-3 text-sm bg-amber-50 border border-amber-200 text-amber-800">
          창고(출발지) 주소가 아직 없어 <b>담당 구역 가운데</b>에서 출발하는 순서로 정리했습니다.{" "}
          {role === "admin"
            ? <Link className="underline font-semibold" to="/admin/org">환경 설정에서 창고 주소 넣기</Link>
            : "사장님께 환경 설정에서 창고 주소를 넣어 달라고 해 주세요."}
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">🧭 최적 경로 ({ym})</h1>
        <div className="flex items-center gap-2 text-sm">
          <div className="inline-flex rounded-lg border bg-white text-xs overflow-hidden">
            <button onClick={() => setView("list")} className={clsx("px-3 py-1.5", view === "list" ? "bg-milk-500 text-white" : "text-slate-600")}>리스트</button>
            <button onClick={() => setView("map")} className={clsx("px-3 py-1.5", view === "map" ? "bg-milk-500 text-white" : "text-slate-600")}>지도</button>
          </div>
          <span className="text-slate-500">길찾기:</span>
          <select className="input w-auto text-sm py-1" value={nav} onChange={(e) => setNav(e.target.value as any)}>
            <option value="tmap">티맵 (추천)</option>
            <option value="kakao">카카오내비</option>
          </select>
        </div>
      </div>

      {myClaims.length === 0 ? (
        <div className="card p-6 text-center text-slate-500">
          담당 동 이름이 없습니다. <Link to="/driver/claim" className="text-milk-600 underline">담당 선택으로 이동</Link>
        </div>
      ) : (
        <>
          <div className="card p-3 text-sm flex flex-wrap gap-2 items-center">
            <span>총 {myStudents.length}가구</span>
            <span>·</span>
            <span>지도 위치 있음 {withCoords.length}/{myStudents.length}</span>
            <span>·</span>
            <span className="text-emerald-600 font-semibold">✅ 배송완료 {doneCount}/{myStudents.length}</span>
            <div className="ml-auto flex gap-2 flex-wrap">
              {missingCoords.length > 0 && (
                <button className="btn-secondary text-xs" onClick={geocodeAll} disabled={geocoding}>
                  {geocoding ? "위치 찾는 중…" : `남은 ${missingCoords.length}집 지도 위치 찾기`}
                </button>
              )}
              <button
                className="btn-primary text-xs"
                onClick={runAiOptimize}
                disabled={aiBusy || pendingWithCoords.length === 0}
                title="현재 위치 기준으로 남은 배송을 최단 동선으로 정렬"
              >
                {aiBusy ? "정렬 중…" : "🧭 최단 경로 정렬"}
              </button>
            </div>
          </div>
          {log && <div className="text-xs text-slate-500">{log}</div>}
          {aiError && <div className="text-xs text-rose-600">⚠️ {aiError}</div>}

          {routeDiag && (
            <div className="card p-3 text-xs font-mono border border-violet-300 bg-violet-50 space-y-0.5">
              <div className="font-bold text-violet-700">🔬 동선 진단 (debug=route) · 미배송 {routeDiag.n}가구</div>
              <div>2-opt 최적해(단지묶기 전): {routeDiag.dRaw.toFixed(2)} km</div>
              <div>단지 묶기 후(현재 표시): {routeDiag.dPacked.toFixed(2)} km</div>
              <div className={routeDiag.delta > 0.01 ? "text-rose-600 font-bold" : "text-emerald-700 font-bold"}>
                Δ {routeDiag.delta >= 0 ? "+" : ""}{routeDiag.delta.toFixed(2)} km
                {routeDiag.delta > 0.01 ? " — 단지 묶기가 동선을 늘림 ⚠️" : " — 단지 묶기 무해/개선 ✅"}
              </div>
              <div className="text-violet-400">※ 직선거리 기준 상대 비교. 실제 도로거리는 2단계에서 교체 예정.</div>
            </div>
          )}

          {/* 지도 위치 미상 — 경로에서 빠지므로 숨기지 않고 명시 노출 */}
          {missingCoords.length > 0 && (
            <div className="card p-3 border border-rose-200 bg-rose-50">
              <div className="text-sm font-bold text-rose-700">⚠️ 지도 위치 미상 {missingCoords.length}명 — 경로에 표시 안 됨</div>
              <p className="text-xs text-rose-600 mt-0.5">위 「지도 위치 찾기」 버튼을 누르거나 사장님께 주소 확인을 요청하세요.</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {missingCoords.map((s) => (
                  <span key={s.id} className="chip bg-white border border-rose-200 text-rose-700 text-2xs">
                    {s.school} {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(aiReasoning || aiGroups.length > 0 || aiWarnings.length > 0) && (
            <div className="card p-3 bg-milk-50 border-milk-200 text-sm space-y-2">
              <div className="flex items-center gap-2">
                <span className="chip bg-milk-500 text-white text-[10px]">🧭 최단 경로 적용됨</span>
                <span className="text-xs text-slate-500">가까운 순서로 정렬</span>
              </div>
              {aiReasoning && (
                <div className="text-slate-700 text-sm">{aiReasoning}</div>
              )}
              {aiWarnings.length > 0 && (
                <ul className="text-xs text-amber-700 list-disc list-inside">
                  {aiWarnings.map((w, i) => <li key={i}>⚠️ {w}</li>)}
                </ul>
              )}
              {aiGroups.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 font-semibold">
                    건물별 배송 전략 ({aiGroups.length}개 단지) — 펼쳐보기
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {aiGroups.map((g, i) => (
                      <li key={i} className="bg-white rounded p-2 border border-slate-200">
                        <div className="font-semibold">🏢 {g.buildingName}</div>
                        <div className="text-slate-600">{g.strategy}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{g.stopIds.length}가구</div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {view === "list" && (
            <div className="space-y-1">
              {routeBlocks.map((block) => {
                if (block.type === "household") {
                  const first = renderStop(block.stops[0], block.index);
                  return (
                    <div key={`hh-${block.stops[0].student.id}`}>
                      {first.header}
                      <div className="border-2 border-red-400 bg-red-50/40 rounded-2xl p-2 space-y-1">
                        <div className="flex items-center gap-1 text-xs font-bold text-red-600 px-1">
                          👨‍👩‍👧 같은 집 {block.stops.length}명 · 한 명만 배송하면 함께 완료
                        </div>
                        {block.stops.map((st, k) => (
                          <div key={st.student.id}>{renderStop(st, block.index + k).card}</div>
                        ))}
                      </div>
                    </div>
                  );
                }
                const { header, card } = renderStop(block.stop, block.index);
                return (
                  <div key={block.stop.student.id}>
                    {header}
                    {card}
                  </div>
                );
              })}
              {pendingRoute.length === 0 && withCoords.length === 0 && myStudents.length > 0 && (
                <div className="card p-4 text-center text-slate-500 text-sm">
                  지도 위치가 있는 집이 없어요. 위의 「지도 위치 찾기」 버튼을 눌러 주세요.
                </div>
              )}
              {pendingRoute.length === 0 && doneStops.length > 0 && (
                <div className="card p-4 text-center text-emerald-600 text-sm font-semibold">
                  🎉 담당 가구 배송을 모두 완료했어요!
                </div>
              )}

              {/* 배송완료 — 간략·회색 음영 카드 */}
              {doneStops.length > 0 && (
                <details className="card p-0 overflow-hidden bg-slate-50 border-slate-200 mt-3">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-500 flex items-center gap-2 select-none">
                    ✅ 배송완료 {doneStops.length}가구
                    <span className="text-xs font-normal text-slate-400">— 펼쳐보기</span>
                  </summary>
                  <ul className="divide-y divide-slate-200 border-t border-slate-200">
                    {doneStops.map((st) => {
                      const s = st.student;
                      return (
                        <li key={s.id} className="px-4 py-2 flex items-center gap-2 text-sm text-slate-400">
                          <span className="text-emerald-400 shrink-0">✓</span>
                          <span className="line-through truncate">{s.school} {s.name}</span>
                          {s.buildingName && (
                            <span className="text-xs truncate">
                              {s.buildingName}
                              {s.buildingDong && ` ${s.buildingDong}동`}
                              {s.buildingHo && ` ${fmtHo(s.buildingHo)}`}
                            </span>
                          )}
                          {/* 잘못 찍었으면 여기서 열어 취소한다(4차 점검 17) */}
                          <Link to={`/driver/route/${s.id}`} className="ml-auto shrink-0 text-xs text-milk-600 underline min-h-[36px] leading-[36px]">열기·취소</Link>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              )}
            </div>
          )}

          {view === "map" && (
            <>
              <div className="card p-2 text-xs text-slate-500">
                {originOverride ? "현재 위치" : "출발지(창고)"}에서 번호 순서대로 배송하세요. 마커를 클릭하면 상세/길안내로 이동 가능합니다.
                {doneStops.length > 0 && <span className="ml-1 text-emerald-600 font-semibold">초록 점 = 배송완료(동선 제외)</span>}
              </div>
              <MapView height="calc(100vh - 260px)" origin={effOriginCoords} showOrigin={false}>
                {/* 내 위치 — 탑차 마커(현재 위치, 없으면 창고 지도 위치) */}
                <Marker position={effOriginCoords} icon={truckIcon()}>
                  <Popup>{originOverride ? "🚚 내 위치(현재)" : "🚚 출발지(창고)"}</Popup>
                </Marker>
                {/* 배송완료 — 초록 점, 동선(폴리라인)에는 포함하지 않음 */}
                {doneStops.map((stop) => (
                  <Marker
                    key={`done-${stop.student.id}`}
                    position={[stop.lat, stop.lng]}
                    icon={dotIcon(STATUS_COLORS.done, 12)}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold text-emerald-600">✅ 배송완료 · {stop.student.school} {stop.student.name}</div>
                        <div>{stop.student.refinedAddress}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {route.map((stop, i) => (
                  <Marker
                    key={stop.student.id}
                    position={[stop.lat, stop.lng]}
                    icon={numberIcon(i + 1, i === 0 ? "#ef4444" : "#1f66ea")}
                    eventHandlers={{
                      click: () => {
                        // 네비(티맵) 띄우고, 사이트는 배송 상세로 전환해 복귀 시 바로 완료 처리.
                        openNavigation({ lat: stop.lat, lng: stop.lng, name: stop.student.refinedAddress }, nav);
                        navigate(`/driver/route/${stop.student.id}`);
                      },
                    }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold">{i + 1}번 · {stop.student.school} {stop.student.name}</div>
                        <div>{stop.student.refinedAddress}</div>
                        <div className="mt-1 flex gap-2">
                          <button
                            className="text-milk-600 underline"
                            onClick={(e) => { e.stopPropagation(); openNavigation({ lat: stop.lat, lng: stop.lng, name: stop.student.refinedAddress }, nav); }}
                          >길안내</button>
                          <button
                            className="text-emerald-600 underline"
                            onClick={(e) => { e.stopPropagation(); navigate(`/driver/route/${stop.student.id}`); }}
                          >배송하기</button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {route.length > 0 && (
                  <Polyline
                    positions={[effOriginCoords, ...route.map((s) => [s.lat, s.lng] as [number, number])]}
                    pathOptions={{ color: "#1f66ea", weight: 3, opacity: 0.6, dashArray: "6 8" }}
                  />
                )}
                <FitBoundsOnData points={[effOriginCoords, ...route.map((s) => [s.lat, s.lng] as [number, number]), ...doneStops.map((s) => [s.lat, s.lng] as [number, number])]} />
              </MapView>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StopCard({
  index, stop, lineColor, onGo, onDetail,
}: { index: number; stop: Stop; lineColor?: number; onGo: () => void; onDetail: () => void }) {
  const s = stop.student;
  const phone = (s.phone || "").split(",")[0].replace(/[^0-9]/g, "");
  const colorClass =
    lineColor === 0 ? "bg-brand-500" :
    lineColor === 1 ? "bg-amber-500" :
    lineColor === 2 ? "bg-emerald-500" : "bg-slate-500";
  return (
    <div
      onClick={onDetail}
      className="card p-3 flex items-start gap-3 hover:shadow-elevated transition cursor-pointer"
    >
      <div className={clsx("w-10 h-10 rounded-full text-white font-bold flex items-center justify-center shrink-0", colorClass)}>
        {index}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold flex items-center gap-1.5 flex-wrap">
          <span className="text-milk-700">{s.school} {s.name}</span>
          {(s.buildingName || s.buildingDong || s.buildingHo) && (
            <span className="chip-neutral">{s.buildingName} {s.buildingDong && `${s.buildingDong}동`} {fmtHo(s.buildingHo)}</span>
          )}
          <span className={`chip text-[10px] ${BRAND_COLORS[s.brand] ?? "bg-slate-100"}`}>{s.brand}</span>
        </div>
        <div className="text-xs text-slate-500 leading-snug break-keep">{s.refinedAddress}</div>
        {/* 배송메모는 상세를 열어야만 보였다 — 목록에서 바로 길안내를 누르면 영영 못 보므로 여기에도 띄운다. */}
        {s.deliveryMemo && (
          <div className="mt-1.5 rounded-lg border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-900 break-keep">
            📌 {s.deliveryMemo}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {/* 카드 아무 데나 눌러도 열리지만, 60대 기사에게는 눌러야 한다는 표시가 없었다(3차 예행연습 2번) */}
        <button className="btn-primary px-5 py-3 text-base font-bold" onClick={(e) => { e.stopPropagation(); onDetail(); }}>📷 배송하기</button>
        <button className="btn-secondary px-5 py-3 text-base font-bold" onClick={(e) => { e.stopPropagation(); onGo(); }}>🧭 길안내</button>
        {phone && (
          <a
            href={`tel:${phone}`}
            onClick={(e) => e.stopPropagation()}
            className="btn-secondary px-5 py-3 text-base font-bold text-center"
          >
            📞 전화
          </a>
        )}
      </div>
    </div>
  );
}
